import {
  Grid,
  MenuItem,
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Box,
  FormHelperText,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { StyledSelect, StyledTextField } from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useFormik } from "formik";
import * as yup from "yup";
import { useCreateClassReportMutation, useLazyGetClassReportQuery, useDeleteClassReportMutation, useGetReportStatusQuery, useCancelReportMutation } from "./classReportApiSlice";
import { useGetClassesPaginatedQuery, useGetClassesQuery } from "../classroom/classApiSlice";
import { useGetSessionsPaginatedQuery, useGetCurrentTermQuery } from "../settings/session/sessionApiSlice";
import { useGetSubSectionQuery } from "../settings/sections/sectionsApiSlice";
import toast from "../../../helpers/toast";
import { PictureAsPdf, Visibility, Close as CloseIcon, Refresh as RefreshIcon, Delete as DeleteIcon, Print as PrintIcon } from "@mui/icons-material";
import { getApiBaseUrl } from "../../../helpers/apiUrl";
import ensureLightReportPreviewHtml, {
  REPORT_PREVIEW_WIDTH,
  printReportPreviewHtml,
  printReportPreviewHtmlList,
} from "../../../helpers/ensureLightReportPreviewHtml";


const validationSchema = yup.object({
  class: yup.object().required("Required"),
  session: yup.object().required("Required"),
  term: yup.string().required("Required"),
});

function ClassReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [createClassReport, { isLoading: isCreatingClassReport }] =
    useCreateClassReportMutation();
  const [fetchClassReports, { isFetching: isFetchingClassReports }] = useLazyGetClassReportQuery();
  const [deleteClassReport] = useDeleteClassReportMutation();
  const [cancelReport, { isLoading: isCancelling }] = useCancelReportMutation();
  const [classReports, setClassReports] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [reportTaskId, setReportTaskId] = useState(null);
  const [reportTaskDone, setReportTaskDone] = useState(false);
  const [reportProgress, setReportProgress] = useState(null);
  const [openClassPreviewModal, setOpenClassPreviewModal] = useState(false);
  const [mobilePreviewTab, setMobilePreviewTab] = useState(0);
  const [loadedStudentReports, setLoadedStudentReports] = useState({});
  const [loadingStudentReports, setLoadingStudentReports] = useState({});
  const [previewErrors, setPreviewErrors] = useState({});
  const [iframeHeights, setIframeHeights] = useState({});
  const iframeRefs = useRef({});
  const pendingParamsRef = useRef(null);
  const { data: currentTerm } = useGetCurrentTermQuery();

  const handleCancel = async () => {
    if (!reportTaskId) return;
    try {
      await cancelReport(reportTaskId).unwrap();
      setReportTaskId(null);
      setReportTaskDone(true);
      setReportProgress(null);
      pendingParamsRef.current = null;
      toast.info('Report generation cancelled.');
    } catch {
      toast.error('Failed to cancel.');
    }
  };

  const isPolling = reportTaskId && !reportTaskDone;
  const { data: reportStatusData } = useGetReportStatusQuery(reportTaskId, {
    skip: !reportTaskId || reportTaskDone,
    pollingInterval: isPolling ? 3000 : 0,
  });

  useEffect(() => {
    if (!reportStatusData) return;
    const { state, info } = reportStatusData;
    if (state === 'PROGRESS' && info) {
      setReportProgress(info);
    } else if (state === 'SUCCESS') {
      setReportTaskDone(true);
      setReportProgress(null);
      toast.success('Class report generated successfully');
      if (pendingParamsRef.current) {
        const { classroom, params } = pendingParamsRef.current;
        fetchClassReports(params).then(({ data: reports }) => {
          if (reports && reports.length > 0) {
            setClassReports(reports.map(r => ({ classroom, report: r })));
          }
        });
        pendingParamsRef.current = null;
      }
      setReportTaskId(null);
    } else if (state === 'FAILURE') {
      setReportTaskDone(true);
      setReportProgress(null);
      const errMsg = typeof info === 'string' ? info : 'Report generation failed';
      toast.error(errMsg);
      setReportTaskId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStatusData]);

  const handlePreview = (url) => {
    setPreviewUrl(url);
  };

  const handleDelete = async (item) => {
    const willDelete = window.confirm("This will permanently delete this report.");
    if (willDelete) {
      try {
        await deleteClassReport({ id: item.report?.id }).unwrap();
        setClassReports(prev => prev.filter(r => r.report?.id !== item.report?.id));
        toast.success("Report has been deleted.");
      } catch (error) {
        toast.error(error?.message || "Failed to delete report");
      }
    }
  };

  const resetPreviewState = () => {
    setLoadedStudentReports({});
    setLoadingStudentReports({});
    setPreviewErrors({});
    setIframeHeights({});
  };

  const setPreviewIframeRef = (studentId) => (node) => {
    if (node) {
      iframeRefs.current[studentId] = node;
    } else {
      delete iframeRefs.current[studentId];
    }
  };

  const measurePreviewIframeHeight = (studentId) => {
    const iframe = iframeRefs.current[studentId];
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const body = doc.body;
      const html = doc.documentElement;
      const rawHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.clientHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      );

      if (rawHeight > 0) {
        const paddedHeight = Math.ceil(rawHeight) + 8;
        setIframeHeights((prev) =>
          prev[studentId] === paddedHeight ? prev : { ...prev, [studentId]: paddedHeight }
        );
      }
    } catch {
      // ignore measurement errors for uncommon iframe rendering edge cases
    }
  };

  const handlePreviewIframeLoad = (studentId) => {
    measurePreviewIframeHeight(studentId);
    window.setTimeout(() => measurePreviewIframeHeight(studentId), 50);
    window.setTimeout(() => measurePreviewIframeHeight(studentId), 300);
    window.setTimeout(() => measurePreviewIframeHeight(studentId), 850);
    window.setTimeout(() => measurePreviewIframeHeight(studentId), 1500);
  };

  const handleOpenClassPreviewModal = () => {
    resetPreviewState();
    setMobilePreviewTab(0);
    setOpenClassPreviewModal(true);
  };

  const handleCloseClassPreviewModal = () => {
    setOpenClassPreviewModal(false);
  };

  const loadSingleStudentPreview = async (student) => {
    if (!student?.id || !formik.values.class?.id || !formik.values.term || !formik.values.template) {
      return;
    }
    if (loadingStudentReports[student.id]) return;

    setLoadingStudentReports((prev) => ({ ...prev, [student.id]: true }));
    setPreviewErrors((prev) => ({ ...prev, [student.id]: null }));

    try {
      const BASE_URL = getApiBaseUrl();
      const token = (localStorage.getItem("access_token") || "").replace(/"/g, "");
      const params = new URLSearchParams({
        preview: "html",
        student_id: String(student.id),
        term_id: String(formik.values.term),
        classroom_id: String(formik.values.class.id),
        template_id: String(formik.values.template),
        subsection_id: String(formik.values.class?.subsection_id || ""),
      });
      const req = await fetch(`${BASE_URL}/report/student?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!req.ok) {
        throw new Error("Failed to load student report preview");
      }
      const htmlText = await req.text();
      setLoadedStudentReports((prev) => ({ ...prev, [student.id]: ensureLightReportPreviewHtml(htmlText) }));
    } catch (error) {
      setPreviewErrors((prev) => ({ ...prev, [student.id]: error?.message || "Error loading preview" }));
    } finally {
      setLoadingStudentReports((prev) => ({ ...prev, [student.id]: false }));
    }
  };

  const handleLoadAllPreviews = async () => {
    for (const student of studentPreviewList) {
      if (!loadedStudentReports[student.id]) {
        // sequential loading avoids sudden browser/resource spikes on large classes
        // eslint-disable-next-line no-await-in-loop
        await loadSingleStudentPreview(student);
      }
    }
  };

  const getStudentDisplayName = (student) =>
    `${student.first_name || ""} ${student.last_name || ""}`.trim() || "Unnamed Student";

  const handlePrintStudentPreview = (student) => {
    const html = loadedStudentReports[student.id];
    if (!html) {
      toast.info("Load this student report before printing.");
      return;
    }

    const didOpen = printReportPreviewHtml(html, `${getStudentDisplayName(student)} Report Preview`);
    if (!didOpen) {
      toast.error("Unable to open print preview. Please allow pop-ups and try again.");
    }
  };

  const handlePrintLoadedPreviews = () => {
    const loadedReports = studentPreviewList
      .map((student) => loadedStudentReports[student.id])
      .filter(Boolean);

    if (loadedReports.length === 0) {
      toast.info("Load at least one report before printing.");
      return;
    }

    const didOpen = printReportPreviewHtmlList(
      loadedReports,
      `${formik.values.class?.name || "Class"} Report Preview`
    );
    if (!didOpen) {
      toast.error("Unable to open print preview. Please allow pop-ups and try again.");
    }
  };

  const ReportPreviewModal = ({ url, open, onClose }) => {
    const [iframeLoading, setIframeLoading] = useState(true);
    const previewTheme = useTheme();
    const isMobilePreview = useMediaQuery(previewTheme.breakpoints.down("sm"));
    const googleDocsUrl = url && !isMobilePreview
      ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
      : null;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobilePreview}
        PaperProps={{ sx: isMobilePreview ? {} : { height: "90vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          Report Preview
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobilePreview ? 3 : 0, overflow: isMobilePreview ? "auto" : "hidden", position: "relative", bgcolor: "#fff" }}>
          {isMobilePreview ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 2, pt: 4 }}>
              <Typography variant="body1" textAlign="center" color="text.secondary">
                Tap below to open or download the report PDF.
              </Typography>
              {url ? (
                <>
                  <Button
                    variant="contained"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<PictureAsPdf />}
                    sx={{ textTransform: "none" }}
                  >
                    Open PDF
                  </Button>
                  <Button
                    variant="outlined"
                    component="a"
                    href={url}
                    download
                    sx={{ textTransform: "none" }}
                  >
                    Download PDF
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary">No report available.</Typography>
              )}
            </Box>
          ) : (
            <>
              {iframeLoading && googleDocsUrl && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.85)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {googleDocsUrl && (
                <iframe
                  key={url}
                  src={googleDocsUrl}
                  title="Report Preview"
                  width="100%"
                  height="100%"
                  style={{ border: "none", backgroundColor: "#fff" }}
                  onLoad={() => setIframeLoading(false)}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const formik = useFormik({
    initialValues: {
      class: "",
      session: "",
      term: "",
      template: "",
      outputMode: "download",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (values.outputMode === "preview") {
        if (!values.template) {
          toast.info("Please select a template to preview class reports.");
          return;
        }
        handleOpenClassPreviewModal();
        return;
      }

      if (!values.template) {
        const { data: reports, error } = await fetchClassReports({ classroom_id: values.class.id, term_id: values.term });
        if (error || !reports || reports.length === 0) {
          toast.info("No reports found for this class and term. Please select a template to generate one.");
          return;
        }
        setClassReports(reports.map(r => ({ classroom: values.class, report: r })));
        return;
      }
      try {
        const body = {
          term_id: values.term,
          classroom_id: values.class.id,
          template_id: values.template,
          regenerate: isRegenerating,
        };
        setIsRegenerating(false);

        const response = await createClassReport(body).unwrap();
        if (response.id) {
          setClassReports([{ classroom: values.class, report: response }]);
          toast.success("Report generated successfully");
        } else if (response.task_id) {
          pendingParamsRef.current = {
            classroom: values.class,
            params: { classroom_id: values.class.id, term_id: values.term },
          };
          setReportTaskDone(false);
          setReportTaskId(response.task_id);
          toast.info('Generating class report...');
        } else if (response.message) {
          toast.success(`Generating ${
              values.session.terms?.find((item) => item.id === values.term).name
            } Report for ${values.class.name}.., come back later`);
        } else {
          toast.error("Broadsheet generation failed");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const studentListRoute = formik.values.class?.id
    ? `${formik.values.class.id}/students${formik.values.session?.id ? `?session_id=${formik.values.session.id}` : ""}`
    : "";
  const { data: classStudents, isLoading: isLoadingClassStudents } = useGetClassesQuery(
    studentListRoute,
    { skip: !openClassPreviewModal || !formik.values.class?.id }
  );
  const studentPreviewList = classStudents || [];
  const loadedPreviewCount = studentPreviewList.filter((student) => loadedStudentReports[student.id]).length;
  const allPreviewsLoaded = studentPreviewList.length > 0 && loadedPreviewCount === studentPreviewList.length;

  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [sessionSearch, setSessionSearch] = React.useState("");
  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 5, q: sessionSearch });
  const sessions = sessionsData?.sessions ?? [];
  const { data: subsection } = useGetSubSectionQuery(
    formik.values.class?.subsection_id,
    { skip: !formik.values.class?.subsection_id }
  );

  // Auto-select current term when session changes
  React.useEffect(() => {
    const terms = formik.values.session?.terms;
    if (!terms || !currentTerm) return;
    const matchingTerm = terms.find(t => t.id === currentTerm.id);
    formik.setFieldValue("term", matchingTerm ? matchingTerm.id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.session?.id, currentTerm?.id]);

  return (
    <div style={{ padding: "0 10px" }}>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Report Form
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.class && formik.errors.class}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">CLASS</Typography>
                    <Autocomplete
                      options={classrooms}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={formik.values.class || null}
                      onChange={(_, value) => formik.setFieldValue("class", value || "")}
                      onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search class..."
                          error={formik.touched.class && Boolean(formik.errors.class)}
                        />
                      )}
                    />
                    <FormHelperText error>
                      {formik.touched.class && formik.errors.class}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.session && formik.errors.session}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">SESSION</Typography>
                    <Autocomplete
                      options={sessions}
                      getOptionLabel={(option) => option.name || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={formik.values.session || null}
                      onChange={(_, value) => formik.setFieldValue("session", value || "")}
                      onInputChange={(_, value, reason) => {
                        if (reason === "input") setSessionSearch(value);
                      }}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          placeholder="Search session..."
                          error={formik.touched.session && Boolean(formik.errors.session)}
                        />
                      )}
                    />
                    <FormHelperText error>
                      {formik.touched.session && formik.errors.session}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.term && formik.errors.term}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TERM</Typography>
                    <StyledSelect
                      displayEmpty
                      id="term"
                      name="term"
                      onChange={formik.handleChange}
                      value={formik.values.term}
                      error={formik.touched.term && Boolean(formik.errors.term)}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a term</em>
                      </MenuItem>
                      {formik.values.session?.terms?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}{currentTerm?.id === item.id ? " (Current)" : ""}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText error>
                      {formik.touched.term && formik.errors.term}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">OUTPUT</Typography>
                    <StyledSelect
                      id="outputMode"
                      name="outputMode"
                      value={formik.values.outputMode}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="download">Download</MenuItem>
                      <MenuItem value="preview">Preview</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.template && formik.errors.template}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">TEMPLATE</Typography>
                    <StyledSelect
                      id="template"
                      name="template"
                      value={formik.values.template}
                      onChange={formik.handleChange}
                      error={formik.touched.template && Boolean(formik.errors.template)}
                    >
                      <MenuItem value="">
                        <em>(No template)</em>
                      </MenuItem>
                      {subsection?.templates?.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText error>
                      {formik.touched.template && formik.errors.template}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12} textAlign={"right"}>
                  <StyledLoadingButton
                    loading={isCreatingClassReport || isFetchingClassReports || isPolling}
                    disabled={isPolling}
                    type="submit"
                    sx={{ textTransform: "none" }}
                  >
                    {isPolling
                      ? 'Generating...'
                      : formik.values.outputMode === "preview"
                        ? 'Preview'
                        : 'Get Report'}
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Generated Table */}

        <Grid item xs={12} md={8}>
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            <Box sx={{ padding: "15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="formHeading">Class Report</Typography>
                <Typography
                  variant="formSubHeading"
                  sx={{ textTransform: "none" }}
                >
                  Select a class to view report
                </Typography>
              </Box>
              {classReports.length > 0 && (
                <Tooltip title="Regenerate report with current settings">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        setIsRegenerating(true);
                        formik.submitForm();
                      }}
                      disabled={isCreatingClassReport || isFetchingClassReports}
                      sx={{ textTransform: "none" }}
                    >
                      Regenerate
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            <TableContainer sx={{ overflowX: "auto" }}>
              {isPolling && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {reportProgress?.total > 0
                        ? (() => {
                            const nameMatch = reportProgress.status?.match(/^Generating (.+?)\.\.\.$/);
                            const name = nameMatch ? nameMatch[1] : null;
                            return name
                              ? `Generating report ${reportProgress.current} of ${reportProgress.total} — ${name}`
                              : `${reportProgress.status || 'Generating...'} (${reportProgress.current}/${reportProgress.total})`;
                          })()
                        : (reportProgress?.status || 'Generating class report...')}
                    </Typography>
                  </Box>
                  {reportProgress?.total > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((reportProgress.current / reportProgress.total) * 100)}
                    />
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isCancelling}
                      sx={{ textTransform: 'none' }}
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  </Box>
                </Box>
              )}
              {isMobile ? (
                <Box sx={{ px: 2, pb: 1 }}>
                  {classReports.map((item, index) => (
                    <Box key={item.report?.id || index} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {item.classroom?.name || item.report?.classroom?.name || "—"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Term: {item.report?.term?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Session: {item.report?.session?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">Template: {item.report?.template?.name || "—"}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                        {item.report?.file_url ? (
                          <>
                            <Tooltip title="Preview report">
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(item.report?.file_url)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download report">
                              <IconButton
                                size="small"
                                component="a"
                                href={item.report?.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={`${item.classroom?.name}.pdf`}
                              >
                                <PictureAsPdf fontSize="small" style={{ color: "red" }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : item.report?.requires_parent_payment ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            PDF disabled in Parent-Pays mode — use HTML Preview
                          </Typography>
                        ) : null}
                        <Tooltip title="Delete report">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "2px solid lightgrey " }}>
                      <StyledTableCell>CLASS</StyledTableCell>
                      <StyledTableCell>TERM</StyledTableCell>
                      <StyledTableCell>SESSION</StyledTableCell>
                      <StyledTableCell>TEMPLATE</StyledTableCell>
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classReports.map((item, index) => (
                      <StyledTableRow key={item.report?.id || index}>
                        <StyledTableCell>
                          {item.classroom?.name || item.report?.classroom?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.term?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.session?.name || "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {item.report?.template?.name || "—"}
                        </StyledTableCell>

                        <StyledTableCell>
                          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            {item.report?.file_url ? (
                              <>
                                <Tooltip title="Preview report">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePreview(item.report?.file_url)}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download report">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={item.report?.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={`${item.classroom?.name}.pdf`}
                                  >
                                    <PictureAsPdf fontSize="small" style={{ color: "red" }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : item.report?.requires_parent_payment ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                PDF disabled in Parent-Pays mode — use HTML Preview
                              </Typography>
                            ) : null}
                            <Tooltip title="Delete report">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </StyledPaper>
        </Grid>
      </Grid>
      <ReportPreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />

      <Dialog
        open={openClassPreviewModal}
        onClose={handleCloseClassPreviewModal}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { height: isMobile ? "100%" : "90vh", display: "flex", flexDirection: "column" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6">Class Report Preview</Typography>
            <Typography variant="body2" color="text.secondary">
              {formik.values.class?.name || "Class"} • {loadedPreviewCount}/{studentPreviewList.length} loaded
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Print loaded reports">
              <span>
                <IconButton
                  size="small"
                  onClick={handlePrintLoadedPreviews}
                  disabled={loadedPreviewCount === 0}
                >
                  <PrintIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={handleCloseClassPreviewModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {isMobile && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs value={mobilePreviewTab} onChange={(_, v) => setMobilePreviewTab(v)}>
              <Tab label={`Students (${loadedPreviewCount}/${studentPreviewList.length})`} />
              <Tab label={`Preview${loadedPreviewCount > 0 ? ` (${loadedPreviewCount})` : ""}`} />
            </Tabs>
          </Box>
        )}
        <DialogContent sx={{ pt: 1, pb: 1, flex: 1, minHeight: 0, overflow: "hidden" }}>
          <Grid container spacing={2} sx={{ height: "100%" }}>
            <Grid item xs={12} md={4} lg={3} sx={isMobile && mobilePreviewTab !== 0 ? { display: "none" } : { height: "100%", minHeight: 0 }}>
              <StyledPaper sx={{ p: 1.5, height: "100%", minHeight: 0, overflowY: "auto" }}>
                <Typography variant="formHeading" sx={{ mb: 1 }}>Students</Typography>
                {isLoadingClassStudents && <LinearProgress sx={{ mb: 1 }} />}
                {!isLoadingClassStudents && studentPreviewList.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No students found in this class.</Typography>
                )}
                {studentPreviewList.map((student) => {
                  const fullName = getStudentDisplayName(student);
                  const isLoaded = Boolean(loadedStudentReports[student.id]);
                  const isLoadingItem = Boolean(loadingStudentReports[student.id]);
                  return (
                    <Box
                      key={student.id}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                        mb: 1,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: ".9rem" }}>{fullName}</Typography>
                      {student.reg_number && (
                        <Typography variant="caption" color="text.secondary">{student.reg_number}</Typography>
                      )}
                      {previewErrors[student.id] && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                          {previewErrors[student.id]}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Button
                          size="small"
                          variant={isLoaded ? "outlined" : "contained"}
                          onClick={() => { loadSingleStudentPreview(student); if (isMobile) setMobilePreviewTab(1); }}
                          disabled={isLoadingItem}
                          sx={{ textTransform: "none" }}
                        >
                          {isLoadingItem ? "Loading..." : isLoaded ? "Reload" : "Load"}
                        </Button>
                        {isLoaded && (
                          <Typography variant="caption" color="success.main">Loaded</Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </StyledPaper>
            </Grid>

            <Grid item xs={12} md={8} lg={9} sx={isMobile && mobilePreviewTab !== 1 ? { display: "none" } : { height: "100%", minHeight: 0 }}>
              <StyledPaper sx={{ p: 1.5, height: "100%", minHeight: 0, overflowY: "auto" }}>
                {allPreviewsLoaded && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    All student reports loaded. Scroll to review.
                  </Typography>
                )}
                {studentPreviewList
                  .filter((student) => loadedStudentReports[student.id] || loadingStudentReports[student.id])
                  .map((student) => {
                    const html = loadedStudentReports[student.id];
                    const isLoadingPreview = Boolean(loadingStudentReports[student.id]);
                    const fullName = getStudentDisplayName(student);
                    return (
                      <Box key={student.id} sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontWeight: 600 }}>{fullName}</Typography>
                          {html && (
                            <Tooltip title="Print report">
                              <IconButton size="small" onClick={() => handlePrintStudentPreview(student)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {isLoadingPreview && !html ? (
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Box sx={{ overflowX: "auto", bgcolor: "#fff" }}>
                            <Box sx={{ width: REPORT_PREVIEW_WIDTH, maxWidth: "none", mx: "auto" }}>
                              <iframe
                                ref={setPreviewIframeRef(student.id)}
                                title={`Student Report Preview ${student.id}`}
                                srcDoc={html}
                                onLoad={() => handlePreviewIframeLoad(student.id)}
                                scrolling="no"
                                style={{
                                  width: "100%",
                                  height: iframeHeights[student.id] || 900,
                                  border: "none",
                                  display: "block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                {!isLoadingClassStudents && loadedPreviewCount === 0 && (
                  <Box sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography color="text.secondary">Load a student report from the list to start previewing.</Typography>
                  </Box>
                )}
              </StyledPaper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1.5,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff"),
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => { handleLoadAllPreviews(); if (isMobile) setMobilePreviewTab(1); }}
            disabled={isLoadingClassStudents || studentPreviewList.length === 0}
            sx={{ textTransform: "none" }}
          >
            Load All
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseClassPreviewModal}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ClassReport;
